import React, { useState, useEffect, useMemo } from 'react';
import { RoleBadge } from './shared/RoleField';
import API_URL from '../config';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import confetti from 'canvas-confetti';
import FaProofModal from './FaProofModal';
import Avatar from './shared/Avatar';
import RatingRing from './shared/RatingRing';
import { easternRange } from './shared/easternRange';
import './ClientInterviewView.css';
import './CustomerPortal.css';

// Labels renamed September 2026 (display text only -- the underlying `value`/object keys below
// are unchanged, so this does not touch any already-stored candidate data, and none of the
// value-keyed behavior elsewhere (confetti on hired, rejection-reason modal on rejected, the
// backend's own validStatuses whitelist) is affected by a label rename.
const STAGE_LABELS = {
  ready_for_interview: 'Interview Result',
  contacted: 'Withdrew',
  maybe: 'Pending',
  hired: 'Hired',
  rejected: 'Rejected',
};
const STAGE_CLASS = {
  ready_for_interview: 'shortlisted',
  contacted: 'contacted',
  maybe: 'maybe',
  hired: 'hired',
  rejected: 'rejected',
};
const STAGE_OPTIONS = [
  { value: 'ready_for_interview', label: 'Interview Result' },
  { value: 'contacted', label: 'Withdrew' },
  { value: 'maybe', label: 'Pending' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
];
// Renamed September 2026, same as STAGE_LABELS above -- but call_status has no separate
// value/label layer (the stored string IS the label, see PATCH /api/candidates/:id/call-status),
// so unlike Interview Result, a candidate already marked with an old value (e.g. "Not Picked")
// keeps showing that literal old text forever -- only new selections use the new wording. The
// backend's own whitelist for this field was updated to match this exact list.
const CALL_STATUS_OPTIONS = ['Interview Status', 'Interviewed', 'Left VM/ Text', 'No Answer'];

function ClientInterviewView({ user, onLogout, hideHeader = false }) {
  const navigate = useNavigate();
  const roleLower = user.role?.toLowerCase();
  // FA Proof is internal-only -- the backend already omits has_fa_proof from the customer
  // branch of GET /api/candidates/interviews/ready entirely, this just controls the column/UI.
  const isInternalUser = roleLower !== 'customer';
  const canReplaceFaProof = ['admin', 'recruiting_admin'].includes(roleLower);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replaceFaProofCandidate, setReplaceFaProofCandidate] = useState(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('interviewViewMode') || 'table');
  // Table view doesn't work on narrow screens -- force cards regardless of stored preference,
  // and hide the toggle so there's no way to land in the bad table experience on a phone.
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const effectiveViewMode = isMobile ? 'kanban' : viewMode;
  const [openDropdown, setOpenDropdown] = useState(null); // {id, type}
  const [inlineNote, setInlineNote] = useState({ id: null, text: '' });
  const [toast, setToast] = useState(null);
  const [hireConfirmFor, setHireConfirmFor] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    // Table-view menus are position:fixed (see toggleDropdown), so they would drift away
    // from their button on scroll/resize -- close them instead.
    const closeFloating = () => setOpenDropdown(prev => (prev?.pos ? null : prev));
    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', closeFloating, true);
    window.addEventListener('resize', closeFloating);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', closeFloating, true);
      window.removeEventListener('resize', closeFloating);
    };
  }, []);

  const toTitleCase = (str) => {
    if (!str) return str;
    return str.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const truncateEmail = (email, maxLength = 25) => {
    if (!email) return '';
    return email.length <= maxLength ? email : email.substring(0, maxLength) + '...';
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const getChipClass = (value) => {
    if (!value) return 'civ-chip-na';
    const v = value.toLowerCase();
    if (v === 'pass' || v === 'complete') return 'civ-chip-pass';
    if (v === 'pending' || v === 'exp.') return 'civ-chip-pending';
    if (v === 'failed' || v === 'positive') return 'civ-chip-fail';
    return 'civ-chip-na';
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
    return phone;
  };

  const handleToggleView = (mode) => {
    setViewMode(mode);
    localStorage.setItem('interviewViewMode', mode);
  };

  // Fetched as a blob (not a plain <a href>) since the route requires a Bearer token, which a
  // normal anchor navigation can't attach. Opens a blank tab synchronously first so the
  // popup-blocker doesn't kick in once the async fetch resolves.
  const viewFaProof = async (candidateId) => {
    const win = window.open('', '_blank');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/candidates/${candidateId}/fa-proof`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      if (win) win.location.href = url;
    } catch (err) {
      if (win) win.close();
      alert('Could not load FA proof image.');
    }
  };

  const calculateAge = (candidate) => {
    if (candidate.interview_status === 'rejected' || candidate.interview_status === 'hired') return 'DONE';
    if (!candidate.interview_sent_date) return 'N/A';
    const diffDays = Math.floor(Math.abs(new Date() - new Date(candidate.interview_sent_date)) / (1000 * 60 * 60 * 24));
    return `${diffDays} days`;
  };

  const generateCandidateSlug = (candidate) => {
    const firstName = (candidate.candidate_name || '').split(' ')[0] || 'candidate';
    const lastName = (candidate.candidate_name || '').split(' ').slice(1).join('-') || 'profile';
    const companyFirstWord = (candidate.company_name || 'company').split(' ')[0] || 'company';
    const date = candidate.screening_date || candidate.date_added || new Date().toISOString().split('T')[0];
    const [year, month, day] = date.split('-');
    const dateStr = `${month}${day}`;
    const cleanFirst = firstName.toLowerCase().replace(/[^a-z0-9]/g, '');
    const cleanLast = lastName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    const cleanCompany = companyFirstWord.toLowerCase().replace(/[^a-z0-9]/g, '');
    return `${cleanFirst}-${cleanLast}-${cleanCompany}-${dateStr}-${candidate.id}`;
  };

  const copyProfileUrl = (candidate) => {
    const slug = generateCandidateSlug(candidate);
    const url = `https://dashboard.mountainrecruiting.com/${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(candidate.id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => showToast('Failed to copy URL', 'error'));
  };

  // Pipeline funnel
  const [allCandidates, setAllCandidates] = useState([]);
  // P&D peer benchmark (green numbers under the funnel %s) -- null whenever it doesn't apply:
  // no company selected ("All Companies"), or the selected company isn't classified P&D.
  const [benchmark, setBenchmark] = useState(null);

  const fetchBenchmark = async (companyId) => {
    if (!companyId) { setBenchmark(null); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/candidates/interviews/pd-benchmark?companyId=${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBenchmark(res.data.applicable ? res.data : null);
    } catch (err) {
      console.error('Error fetching P&D benchmark:', err);
      setBenchmark(null);
    }
  };

  const fetchAllCandidates = async () => {
    try {
      const token = localStorage.getItem('token');
      let url;
      if (user.role?.toLowerCase() === 'customer') {
        const params = selectedCompany ? `?companyId=${selectedCompany}` : '';
        url = `${API_URL}/api/candidates/customer/${user.id}${params}`;
      } else {
        const params = new URLSearchParams({ all: 'true' });
        if (selectedCompany) params.append('companyId', selectedCompany);
        url = `${API_URL}/api/candidates?${params}`;
      }
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      // /api/candidates returns { candidates, total } since pagination was added
      setAllCandidates(Array.isArray(res.data) ? res.data : (res.data.candidates || []));
    } catch (err) {
      console.error('Error fetching all candidates for funnel:', err);
    }
  };

  const funnel = useMemo(() => {
    // US Eastern calendar days, Monday-Sunday weeks, same rules as the table (server side)
    const { from, to } = easternRange(dateFilter, startDate, endDate);

    const inRange = allCandidates.filter(c => {
      const d = new Date(c.created_at || c.date_added);
      if (from && d < from) return false;
      if (to   && d >= to)  return false;
      return true;
    });
    const screened = inRange.length;
    const fivePlus = inRange.filter(c => (c.mountain_rating || 0) >= 5).length;
    const fadvSent = inRange.filter(c => c.fadv_status?.toLowerCase() === 'sent').length;

    // Filter Sent to Client by interview_sent_date in the same range — matches table
    const sentToClient = allCandidates.filter(c => {
      if (!c.interview_sent_date) return false;
      const d = new Date(c.interview_sent_date);
      if (from && d < from) return false;
      if (to   && d >= to)  return false;
      return true;
    }).length;

    return {
      screened, fivePlus, fadvSent, sentToClient,
      pct5Plus : screened > 0 ? Math.round(fivePlus / screened * 100) : 0,
      pctFadv  : fivePlus > 0 ? Math.round(fadvSent / fivePlus * 100) : 0,
      pctSent  : fadvSent > 0 ? Math.round(sentToClient / fadvSent * 100) : 0,
    };
  }, [allCandidates, dateFilter, startDate, endDate]);

  const fetchUserCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users/my-companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (err) {
      console.error('Error fetching user companies:', err);
    }
  };

  useEffect(() => { fetchUserCompanies(); fetchAllCandidates(); }, []); // eslint-disable-line

  useEffect(() => {
    fetchCandidates();
    fetchAllCandidates();
    const interval = setInterval(fetchCandidates, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, dateFilter, startDate, endDate]);

  // Deliberately its own effect -- the benchmark's 15-day window is fixed and does not follow
  // the page's dateFilter/startDate/endDate. Also deliberately NOT keyed only on selectedCompany:
  // a customer lands on this page with the company filter defaulting to "All Companies"
  // (selectedCompany === ''), and the funnel's own numbers under "All Companies" are already a
  // pooled total across every company that customer has access to (see fetchAllCandidates) --
  // so the benchmark should apply there too whenever that whole set is P&D, not just once
  // they've manually narrowed the dropdown to one company. A customer with a *mixed* set
  // (some P&D, some Linehaul, or some unclassified) gets no benchmark under "All Companies"
  // since their pooled numbers wouldn't be a fair P&D comparison either way -- but still gets
  // one the moment they pick a specific P&D company from the dropdown (the selectedCompany path
  // below, unchanged). Internal staff viewing "All Companies" across many customers still
  // correctly see nothing until they narrow to one.
  useEffect(() => {
    if (selectedCompany) {
      fetchBenchmark(selectedCompany);
    } else if (roleLower === 'customer' && companies.length > 0 && companies.every(c => c.service_type === 'P&D')) {
      fetchBenchmark(companies[0].id);
    } else {
      setBenchmark(null);
    }
  }, [selectedCompany, companies, roleLower]);

  const fetchCandidates = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (user.role?.toLowerCase() === 'customer') params.append('customerId', user.id);
      if (selectedCompany) params.append('companyId', selectedCompany);
      if (dateFilter && dateFilter !== 'all') {
        params.append('dateFilter', dateFilter);
        if (dateFilter === 'custom' && startDate && endDate) {
          params.append('startDate', startDate);
          params.append('endDate', endDate);
        }
      }
      const queryString = params.toString();
      const response = await axios.get(
        `${API_URL}/api/candidates/interviews/ready${queryString ? `?${queryString}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCandidates(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching candidates:', err);
      setLoading(false);
    }
  };

  // ── Action handlers ──

  const handleStageChange = async (candidate, newStage) => {
    setOpenDropdown(null);
    if (newStage === candidate.interview_status) return;
    if (newStage === 'rejected') {
      setSelectedCandidate(candidate);
      setRejectionReason(candidate.rejection_reason || '');
      setShowRejectionModal(true);
      return;
    }
    if (newStage === 'hired') {
      setHireConfirmFor(candidate);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/candidates/${candidate.id}/interview-status`,
        { status: newStage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCandidates();
    } catch (err) {
      showToast('Error updating stage', 'error');
    }
  };

  const confirmHire = async () => {
    const candidate = hireConfirmFor;
    setHireConfirmFor(null);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/candidates/${candidate.id}/interview-status`,
        { status: 'hired' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      confetti({ particleCount: 160, spread: 90, origin: { y: 0.6 }, colors: ['#1e2d5a', '#2563eb', '#16a34a', '#facc15'] });
      fetchCandidates();
    } catch (err) {
      showToast('Error marking as hired', 'error');
    }
  };

  const submitRejection = async () => {
    if (!rejectionReason.trim()) { showToast('Please provide a rejection reason', 'error'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/candidates/${selectedCandidate.id}/interview-status`,
        { status: 'rejected', rejection_reason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowRejectionModal(false);
      setSelectedCandidate(null);
      setRejectionReason('');
      fetchCandidates();
    } catch (err) {
      showToast('Error updating candidate status', 'error');
    }
  };

  const handleCallStatus = async (candidateId, status) => {
    setOpenDropdown(null);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/candidates/${candidateId}/call-status`,
        { call_status: status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCandidates();
    } catch (err) {
      showToast('Error updating call status', 'error');
    }
  };

  const saveNote = async (candidateId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/candidates/${candidateId}/interview-notes`,
        { interview_notes: inlineNote.text || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInlineNote({ id: null, text: '' });
      fetchCandidates();
    } catch (err) {
      showToast('Error saving note', 'error');
    }
  };

  // In the table, .interview-table-wrapper's overflow-x:auto also clips vertically, so a
  // menu opened on one of the last rows was cut off by the table's bottom edge (looked like
  // "the dropdown doesn't open", esp. on Windows). Table menus therefore float (position:
  // fixed at the button's screen position) and open upward when there's no room below.
  // Card view keeps the plain absolute menu: .civ-card's hover transform would break fixed.
  const toggleDropdown = (e, candidateId, type, floating = false) => {
    e.stopPropagation();
    let pos = null;
    if (floating) {
      const r = e.currentTarget.getBoundingClientRect();
      const menuH = 260;
      const up = r.bottom + menuH > window.innerHeight && r.top > menuH;
      pos = up
        ? { position: 'fixed', bottom: window.innerHeight - r.top + 6, top: 'auto', left: r.left, right: 'auto' }
        : { position: 'fixed', top: r.bottom + 6, left: r.left, right: 'auto' };
    }
    setOpenDropdown(prev =>
      prev?.id === candidateId && prev?.type === type ? null : { id: candidateId, type, pos }
    );
  };

  // ── Pill buttons — shared between card (col layout) and table (row layout) ──
  const renderPillButtons = (candidate, layout = 'row', floating = false) => {
    const wrapClass = layout === 'col' ? 'civ-action-col' : 'civ-action-row';
    return (
      <div className={wrapClass} onClick={e => e.stopPropagation()}>
        {/* Call Status */}
        <div className="civ-dropdown-wrap">
          <button
            className={`civ-pill civ-pill-call${candidate.call_status ? ' has-value' : ''}`}
            onClick={e => toggleDropdown(e, candidate.id, 'call', floating)}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21.72 16z"/></svg>
            {candidate.call_status || 'Interview Status'}
            <span className="civ-pill-arrow">▾</span>
          </button>
          {openDropdown?.id === candidate.id && openDropdown?.type === 'call' && (
            <div className="civ-dropdown" style={openDropdown.pos || undefined}>
              {CALL_STATUS_OPTIONS.map(opt => (
                <div key={opt} className={`civ-dropdown-item${candidate.call_status === opt ? ' active' : ''}`} onClick={() => handleCallStatus(candidate.id, opt)}>
                  {opt}{candidate.call_status === opt && <span className="civ-check">✓</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Change Stage */}
        <div className="civ-dropdown-wrap">
          <button
            className={`civ-pill civ-pill-stage ${STAGE_CLASS[candidate.interview_status] || 'shortlisted'}`}
            onClick={e => toggleDropdown(e, candidate.id, 'stage', floating)}
          >
            {STAGE_LABELS[candidate.interview_status] || 'Interview Result'}
            <span className="civ-pill-arrow">▾</span>
          </button>
          {openDropdown?.id === candidate.id && openDropdown?.type === 'stage' && (
            <div className="civ-dropdown" style={openDropdown.pos || undefined}>
              <div className="civ-dropdown-header">Interview Result</div>
              {STAGE_OPTIONS.map(opt => (
                <div key={opt.value} className={`civ-dropdown-item${candidate.interview_status === opt.value ? ' active' : ''}${opt.value === 'rejected' ? ' danger' : ''}`} onClick={() => handleStageChange(candidate, opt.value)}>
                  {opt.label}{candidate.interview_status === opt.value && <span className="civ-check">✓</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add / Edit note */}
        <button
          className={`civ-pill civ-pill-comment${candidate.interview_notes ? ' has-value' : ''}`}
          onClick={e => {
            e.stopPropagation();
            setInlineNote(inlineNote.id === candidate.id ? { id: null, text: '' } : { id: candidate.id, text: candidate.interview_notes || '' });
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          {candidate.interview_notes ? 'Edit note' : 'Add note'}
        </button>
      </div>
    );
  };

  const renderInlineNote = (candidate) => inlineNote.id === candidate.id ? (
    <div className="civ-inline-note-wrap" onClick={e => e.stopPropagation()}>
      <textarea
        className="civ-inline-note-input"
        value={inlineNote.text}
        onChange={e => setInlineNote(prev => ({ ...prev, text: e.target.value }))}
        placeholder="Add a note about this candidate..."
        rows={3}
        autoFocus
        onKeyDown={e => { if (e.key === 'Escape') setInlineNote({ id: null, text: '' }); }}
      />
      <div className="civ-inline-note-actions">
        <button className="civ-note-save" onClick={() => saveNote(candidate.id)}>Save</button>
        <button className="civ-note-cancel" onClick={() => setInlineNote({ id: null, text: '' })}>Cancel</button>
      </div>
    </div>
  ) : null;

  const getScreeningBadge = (value) => {
    if (!value) return <span className="tbl-badge tbl-na">—</span>;
    const v = value.toLowerCase();
    if (v === 'pass' || v === 'complete') return <span className="tbl-badge tbl-pass">{value}</span>;
    if (v === 'pending' || v === 'exp.') return <span className="tbl-badge tbl-pending">{value}</span>;
    if (v === 'failed' || v === 'positive') return <span className="tbl-badge tbl-failed">{value}</span>;
    return <span className="tbl-badge tbl-na">{value}</span>;
  };

  const getStatusBadge = (status) => {
    if (status === 'hired')              return <span className="tbl-badge tbl-hired">Hired</span>;
    if (status === 'rejected')           return <span className="tbl-badge tbl-rejected">Rejected</span>;
    if (status === 'contacted')          return <span className="tbl-badge tbl-contacted">Contacted</span>;
    if (status === 'maybe')              return <span className="tbl-badge tbl-maybe">Maybe</span>;
    return <span className="tbl-badge tbl-ready">Shortlisted</span>;
  };

  const header = (
    <header className="app-header">
      <div className="header-left">
        <img src="/logo.png" alt="Mountain Recruiting" className="logo" />
        <h1>Ready for Interview</h1>
      </div>
      <div className="header-right">
        <span className="user-email">{user.email}</span>
        {user.role?.toLowerCase() === 'customer' ? (
          <>
            <button onClick={() => navigate('/customer-portal')}>Driver Candidates</button>
            <button onClick={() => navigate('/onboarding')}>Onboarding Information</button>
          </>
        ) : (
          <button onClick={() => navigate('/recruiter-portal')}>Back to Portal</button>
        )}
        <button onClick={onLogout} className="logout-btn">Logout</button>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="interview-view">
        {!hideHeader && header}
        <div className="loading-state">Loading candidates...</div>
      </div>
    );
  }

  return (
    <div className="interview-view">
      {!hideHeader && header}

      <div className="interview-content">
        <div className="interview-header interview-header--no-title">
          <div className="interview-header-right">
            {/* Table is the only thing this toggle ever chooses between -- on mobile Table isn't
                an option at all, so a lone "Cards" button would just be a functionless pill.
                Don't render the toggle at all in that case rather than show dead UI. */}
            {!isMobile && (
            <div className="view-toggle">
              <button className={`view-toggle-btn${effectiveViewMode === 'kanban' ? ' active' : ''}`} onClick={() => handleToggleView('kanban')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="2" y="3" width="9" height="9" rx="1"/><rect x="13" y="3" width="9" height="9" rx="1"/><rect x="2" y="14" width="9" height="7" rx="1"/><rect x="13" y="14" width="9" height="7" rx="1"/></svg>
                Cards
              </button>
              <button className={`view-toggle-btn${effectiveViewMode === 'table' ? ' active' : ''}`} onClick={() => handleToggleView('table')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="2" y="3" width="20" height="3" rx="1"/><rect x="2" y="8" width="20" height="3" rx="1"/><rect x="2" y="13" width="20" height="3" rx="1"/><rect x="2" y="18" width="20" height="3" rx="1"/></svg>
                Table
              </button>
            </div>
            )}
            <div className="candidate-count">{candidates.length} Candidate{candidates.length !== 1 ? 's' : ''}</div>
          </div>
        </div>

        {/* Pipeline funnel */}
        <div className="cp-funnel-section civ-funnel-section--bare">
          <div className="cp-funnel-bar">
            <div className="cp-funnel-cell dark" title="Total candidates added in the selected date range (excludes archived/test records).">
              <div className="cp-funnel-metric-label">SCREENED</div><div className="cp-funnel-value">{funnel.screened}</div>
            </div>
            <div className="cp-funnel-cell light" title="% of screened candidates rated 5 or higher by Mountain Recruiting.">
              <div className="cp-funnel-arrow">→ 5+</div><div className="cp-funnel-pct">{funnel.pct5Plus}%</div>
              {benchmark && <div className="cp-funnel-benchmark" title={`P&D customer average, last 15 days (${benchmark.companyCount} companies)`}>{benchmark.pct5Plus}%</div>}
            </div>
            <div className="cp-funnel-cell dark" title="Candidates rated 5 or higher.">
              <div className="cp-funnel-metric-label">5+</div><div className="cp-funnel-value">{funnel.fivePlus}</div>
            </div>
            <div className="cp-funnel-cell light" title="% of 5+-rated candidates who had a background check (FADV) sent.">
              <div className="cp-funnel-arrow">→ FADV</div><div className="cp-funnel-pct">{funnel.pctFadv}%</div>
              {benchmark && <div className="cp-funnel-benchmark" title={`P&D customer average, last 15 days (${benchmark.companyCount} companies)`}>{benchmark.pctFadv}%</div>}
            </div>
            <div className="cp-funnel-cell dark" title="Candidates whose First Advantage (FADV) background check was sent.">
              <div className="cp-funnel-metric-label">FADV SENT</div><div className="cp-funnel-value">{funnel.fadvSent}</div>
            </div>
            <div className="cp-funnel-cell light" title="% of FADV-sent candidates who were sent to the client for interview.">
              <div className="cp-funnel-arrow">→ SENT</div><div className="cp-funnel-pct">{funnel.pctSent}%</div>
              {benchmark && <div className="cp-funnel-benchmark" title={`P&D customer average, last 15 days (${benchmark.companyCount} companies)`}>{benchmark.pctSent}%</div>}
            </div>
            <div className="cp-funnel-cell dark" title="Candidates sent to the client for interview.">
              <div className="cp-funnel-metric-label">SENT TO CLIENT</div><div className="cp-funnel-value">{funnel.sentToClient}</div>
            </div>
          </div>
        </div>

        <div className="interview-filter-bar">
          {companies.length > 0 && (
            <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} className="interview-filter-select">
              <option value="">All Companies</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <select value={dateFilter} onChange={e => { setDateFilter(e.target.value); if (e.target.value !== 'custom') { setStartDate(''); setEndDate(''); } }} className="interview-filter-select">
            <option value="all">All Dates Sent</option>
            <option value="thisWeek">This Week</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="last3Months">Last 3 Months</option>
            <option value="custom">Custom Range</option>
          </select>
          {dateFilter === 'custom' && (
            <>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="interview-filter-date" />
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="interview-filter-date" />
            </>
          )}
          {(selectedCompany || dateFilter !== 'all') && (
            <button className="interview-clear-btn" onClick={() => { setSelectedCompany(''); setDateFilter('all'); setStartDate(''); setEndDate(''); }}>✕ Clear</button>
          )}
        </div>

        {candidates.length === 0 ? (
          <div className="empty-state">No candidates ready for interview yet.</div>
        ) : effectiveViewMode === 'table' ? (

          /* ── TABLE VIEW ── */
          <div className="interview-table-wrapper">
            <table className="interview-table">
              <thead>
                <tr>
                  <th>Candidate Name</th>
                  <th>Date Sent</th>
                  <th>FedEx ID</th>
                  <th>Rating</th>
                  <th>CSA</th>
                  {isInternalUser && <th>FA Proof</th>}
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Resume</th>
                  <th>Actions</th>
                  <th>BGV</th>
                  <th>Drug</th>
                  <th>Drug Pass Date</th>
                  <th>MEC</th>
                  <th>Experience</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((candidate, idx) => {
                  const mountainUrl = candidate.mountain_resume_url ? `/${candidate.mountain_resume_url}` : null;
                  return (
                    <tr key={candidate.id} className={idx % 2 === 0 ? 'tbl-row-even' : 'tbl-row-odd'}>
                      <td className="tbl-name-cell" data-label="Candidate Name">
                        <span className="mtn-name-row">
                          <Avatar name={candidate.candidate_name} size="sm" />
                          {mountainUrl ? (
                            <a href={mountainUrl} target="_blank" rel="noopener noreferrer" className="tbl-candidate-link" title={toTitleCase(candidate.candidate_name)}>
                              {truncateEmail(toTitleCase(candidate.candidate_name), 15)}
                            </a>
                          ) : (
                            <span title={toTitleCase(candidate.candidate_name)}>{truncateEmail(toTitleCase(candidate.candidate_name), 15)}</span>
                          )}
                          <RoleBadge role={candidate.job_role} />
                        </span>
                        {candidate.interview_notes && (
                          <div className="tbl-interview-note">💬 {candidate.interview_notes}</div>
                        )}
                        {candidate.interview_status === 'rejected' && candidate.rejection_reason && (
                          <div className="tbl-rejection">Rejected: {candidate.rejection_reason}</div>
                        )}
                      </td>
                      <td className="tbl-center" data-label="Date Sent">
                        {candidate.interview_sent_date ? new Date(candidate.interview_sent_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                      </td>
                      <td className="tbl-center" data-label="FedEx ID">{candidate.fedex_id || '—'}</td>
                      <td className="tbl-center" data-label="Rating">
                        <RatingRing rating={candidate.mountain_rating || null} />
                      </td>
                      <td className="tbl-location" data-label="CSA">{candidate.csa_name ? candidate.csa_name.split(' — ')[0] : '—'}</td>
                      {isInternalUser && (
                        <td className="tbl-center" data-label="FA Proof">
                          {candidate.fa_proof_overridden ? (
                            <span className="tbl-fa-proof-badge override" title="Sent with an incomplete FA file by GV, AJ, Monica, Atiya or Maddy">Override</span>
                          ) : candidate.fa_proof_passed ? (
                            <span className="tbl-fa-proof-badge passed" title="Screenshot check passed">✓ Checked</span>
                          ) : null}
                          {!candidate.has_fa_proof && candidate.fedex_history && candidate.fadv_file_transfer === 'Not Transferred' && (
                            <span className="tbl-fa-proof-badge expired" title={`${candidate.fedex_history}: FADV file not transferred, sent without an FA screenshot`}>No FA file (not transferred)</span>
                          )}
                          {candidate.fa_proof_expired && !candidate.has_fa_proof && (
                            <span className="tbl-fa-proof-badge expired" title="Screenshots are deleted after 90 days">Expired</span>
                          )}
                          {candidate.has_fa_proof ? (
                            <>
                              <button type="button" className="tbl-fa-proof-link" onClick={() => viewFaProof(candidate.id)}>View</button>
                              {canReplaceFaProof && (
                                <button type="button" className="tbl-fa-proof-replace" onClick={() => setReplaceFaProofCandidate(candidate)}>Replace</button>
                              )}
                            </>
                          ) : canReplaceFaProof ? (
                            <button type="button" className="tbl-fa-proof-replace" onClick={() => setReplaceFaProofCandidate(candidate)}>Upload</button>
                          ) : '—'}
                        </td>
                      )}
                      <td className="tbl-center" data-label="Phone">
                        <a href={`tel:${candidate.phone}`} className="tbl-phone-link">{formatPhone(candidate.phone)}</a>
                      </td>
                      <td data-label="Email">
                        <a href={`mailto:${candidate.email}`} className="tbl-email-link"
                          style={candidate.email?.includes('indeedemail.com') ? { color: '#dc2626', fontWeight: 700 } : {}}
                          title={candidate.email}>
                          {truncateEmail(candidate.email, 12)}
                        </a>
                      </td>
                      <td className="tbl-center" data-label="Resume">
                        {candidate.indeed_url ? <a href={candidate.indeed_url} target="_blank" rel="noopener noreferrer" className="tbl-resume-link">View</a> : '—'}
                      </td>
                      <td className="tbl-actions-cell" data-label="Actions">
                        {renderPillButtons(candidate, 'row', true)}
                        {renderInlineNote(candidate)}
                      </td>
                      <td className="tbl-center" data-label="BGV">{getScreeningBadge(candidate.bg_status)}</td>
                      <td className="tbl-center" data-label="Drug">{getScreeningBadge(candidate.drug_display_status)}</td>
                      <td className="tbl-center" data-label="Drug Pass Date">
                        {candidate.drug_test_pass_date ? new Date(candidate.drug_test_pass_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                      <td className="tbl-center" data-label="MEC">{getScreeningBadge(candidate.physical_status)}</td>
                      <td className="tbl-experience" data-label="Experience">{candidate.last_worked || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        ) : (

          /* ── KANBAN / CARD VIEW ── */
          <div className="candidates-list">
            {candidates.map(candidate => {
              const stageClass = STAGE_CLASS[candidate.interview_status] || 'shortlisted';
              const mountainUrl = candidate.mountain_resume_url ? `/${candidate.mountain_resume_url}` : null;
              return (
                <div key={candidate.id} className={`civ-card ${stageClass}`}
                  style={{ zIndex: (openDropdown?.id === candidate.id || inlineNote.id === candidate.id) ? 10 : 1 }}>

                  {/* Top row: avatar + identity + share */}
                  <div className="civ-card-top">
                    <div className="civ-card-avatar">{getInitials(candidate.candidate_name)}</div>
                    <div className="civ-card-identity">
                      <div className="civ-card-name">
                        {mountainUrl ? (
                          <a href={mountainUrl} target="_blank" rel="noopener noreferrer" className="civ-card-name-link">
                            {toTitleCase(candidate.candidate_name)}
                          </a>
                        ) : toTitleCase(candidate.candidate_name)}
                        <RoleBadge role={candidate.job_role} />
                      </div>
                      <div className="civ-card-meta">
                        {candidate.mountain_rating && (
                          <span className={`civ-rating-badge ${candidate.mountain_rating >= 8 ? 'civ-rating-high' : candidate.mountain_rating >= 5 ? 'civ-rating-mid' : 'civ-rating-low'}`}>
                            {candidate.mountain_rating}
                          </span>
                        )}
                        <span className={`civ-stage-chip ${stageClass}`}>{STAGE_LABELS[candidate.interview_status] || 'Interview Result'}</span>
                        {candidate.fedex_id && <span className="civ-card-fedex">FedEx ID: {candidate.fedex_id}</span>}
                        {candidate.interview_sent_date && (
                          <span className="civ-card-sent">
                            Sent {new Date(candidate.interview_sent_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="share-icon-container">
                      <button onClick={() => copyProfileUrl(candidate)} className="civ-share-btn" title="Share profile">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                        </svg>
                      </button>
                      {copiedId === candidate.id && <span className="copied-tooltip">Copied!</span>}
                    </div>
                  </div>

                  {/* Body: info rows + action pills */}
                  <div className="civ-card-body">
                    <div className="civ-card-info">
                      <div className="civ-info-row">
                        <span className="civ-info-lbl">Experience</span>
                        <span className="civ-info-val civ-info-exp">{candidate.last_worked || '—'}</span>
                      </div>
                      <div className="civ-info-row">
                        <span className="civ-info-lbl">CSA</span>
                        <span className="civ-info-val">{candidate.csa_name || '—'}</span>
                      </div>
                      <div className="civ-info-row">
                        <span className="civ-info-lbl">Cell</span>
                        <span className="civ-info-val">
                          <a href={`tel:${candidate.phone}`} className="phone-link">{formatPhone(candidate.phone)}</a>
                        </span>
                      </div>
                      <div className="civ-info-row">
                        <span className="civ-info-lbl">Email</span>
                        <span className="civ-info-val">
                          <a href={`mailto:${candidate.email}`} className="email-link"
                            style={candidate.email?.includes('indeedemail.com') ? {color: 'red', fontWeight: 'bold'} : {}}
                            title={candidate.email}>
                            {truncateEmail(candidate.email)}
                          </a>
                        </span>
                      </div>
                      <div className="civ-info-row">
                        <span className="civ-info-lbl">Resume</span>
                        <span className="civ-info-val">
                          {candidate.indeed_url ? (
                            <a href={candidate.indeed_url} target="_blank" rel="noopener noreferrer" className="resume-link">View ↗</a>
                          ) : <span style={{color: '#94a3b8'}}>N/A</span>}
                        </span>
                      </div>
                      <div className="civ-info-row">
                        <span className="civ-info-lbl">Drug Pass</span>
                        <span className="civ-info-val">
                          {candidate.drug_test_pass_date
                            ? new Date(candidate.drug_test_pass_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : <span style={{color: '#94a3b8'}}>—</span>}
                        </span>
                      </div>
                    </div>
                    <div className="card-body-right">
                      {/* Mobile: stack card-body-right below the info instead of beside it
                          (fixed 160px column doesn't fit next to info on a phone -- see the
                          .civ-card-body mobile media query), and lay the 3 pills out in a
                          horizontal wrapped row ('row' layout, same as table view uses) instead
                          of stacked ('col') so the actions row stays compact instead of adding
                          3 full-height buttons worth of vertical space. */}
                      {renderPillButtons(candidate, isMobile ? 'row' : 'col')}
                      {renderInlineNote(candidate)}
                    </div>
                  </div>

                  {/* Remarks -- hidden on mobile only per explicit request (scope not stated as
                      "everywhere", so left visible on desktop; flag and correct if that's wrong) */}
                  {candidate.remarks && !isMobile && (
                    <div className="civ-card-remarks">{candidate.remarks}</div>
                  )}

                  {/* Note display */}
                  {candidate.interview_notes && (
                    <div className="civ-card-note">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      {candidate.interview_notes}
                    </div>
                  )}

                  {/* Rejection reason */}
                  {candidate.interview_status === 'rejected' && candidate.rejection_reason && (
                    <div className="civ-card-rejection">{candidate.rejection_reason}</div>
                  )}

                  {/* Footer: BGV / Drug / MEC / Aging chips */}
                  <div className="civ-card-footer">
                    {candidate.bg_status && (
                      <>
                        <div className="civ-status-chip">
                          <span className="civ-status-chip-label">BGV</span>
                          <span className={`civ-status-chip-val ${getChipClass(candidate.bg_status)}`}>{candidate.bg_status}</span>
                        </div>
                        <div className="civ-status-divider" />
                      </>
                    )}
                    {candidate.drug_status && candidate.drug_status !== 'Status' && (
                      <>
                        <div className="civ-status-chip">
                          <span className="civ-status-chip-label">Drug</span>
                          <span className={`civ-status-chip-val ${getChipClass(candidate.drug_display_status || candidate.drug_status)}`}>
                            {candidate.drug_display_status || candidate.drug_status}
                          </span>
                        </div>
                        <div className="civ-status-divider" />
                      </>
                    )}
                    {candidate.physical_status && candidate.physical_status !== 'Status' && (
                      <>
                        <div className="civ-status-chip">
                          <span className="civ-status-chip-label">MEC</span>
                          <span className={`civ-status-chip-val ${getChipClass(candidate.physical_status)}`}>{candidate.physical_status}</span>
                        </div>
                        <div className="civ-status-divider" />
                      </>
                    )}
                    <div className="civ-status-chip">
                      <span className="civ-status-chip-label">Aging</span>
                      <span className={`civ-status-chip-val ${calculateAge(candidate) === 'DONE' || calculateAge(candidate) === 'N/A' ? 'civ-chip-na' : 'civ-chip-aging'}`}>
                        {calculateAge(candidate)}
                      </span>
                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Rejection modal */}
      {showRejectionModal && (
        <div className="modal-overlay" onClick={() => setShowRejectionModal(false)}>
          <div className="modal-content rejection-modal" onClick={e => e.stopPropagation()}>
            <h3>Reject Candidate</h3>
            <p className="modal-desc">Please provide a reason for rejecting {selectedCandidate?.candidate_name}:</p>
            <div className="rejection-quickpick">
              {['No-show', "Didn't pick up phone", 'Not a fit', 'Already placed'].map(r => (
                <button
                  key={r}
                  className={`rejection-qp-btn${rejectionReason === r ? ' active' : ''}`}
                  onClick={() => setRejectionReason(r)}
                >
                  {r}
                </button>
              ))}
            </div>
            <textarea
              value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows="3"
              className="rejection-textarea"
            />
            <div className="modal-actions">
              <button onClick={() => setShowRejectionModal(false)} className="btn-cancel">Cancel</button>
              <button onClick={submitRejection} className="btn-submit">Submit</button>
            </div>
          </div>
        </div>
      )}

      {hireConfirmFor && (
        <div className="cp-confirm-overlay" onClick={() => setHireConfirmFor(null)}>
          <div className="cp-confirm-box" onClick={e => e.stopPropagation()}>
            <h3>Mark as Hired?</h3>
            <p>Confirm that <strong>{hireConfirmFor.candidate_name}</strong> has been hired. This will notify your recruiter.</p>
            <div className="cp-confirm-actions">
              <button className="btn-cancel" onClick={() => setHireConfirmFor(null)}>Cancel</button>
              <button className="btn-hire" onClick={confirmHire}>Yes, Hired!</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`cp-toast ${toast.type}`}>{toast.message}</div>
      )}

      {replaceFaProofCandidate && (
        <FaProofModal
          candidate={replaceFaProofCandidate}
          mode="replace"
          onClose={() => setReplaceFaProofCandidate(null)}
          onSuccess={() => { setReplaceFaProofCandidate(null); fetchCandidates(); }}
        />
      )}

    </div>
  );
}

export default ClientInterviewView;
